import React, { useState, useMemo } from "react";
import {
  MONTHLY_INCOME,
  ESSENTIALS_FLOOR,
  STARTING_DEBT,
  DEBT_APR,
  EMERGENCY_FUND_GOAL,
  computeBaselineSuggestions,
} from "./data.js";

const STEP = 10;

function StepperRow({ label, hint, value, onChange, min = 0, accent = "cyan", disabled = false }) {
  const accentText = {
    cyan: "text-cyan",
    lime: "text-lime",
    gold: "text-gold",
    magenta: "text-magenta",
    danger: "text-danger",
  }[accent];

  return (
    <div className="flex items-center justify-between gap-3 border-b border-black/40 py-3 last:border-0">
      <div className="min-w-0 flex-1">
        <p className={`font-display text-[9px] uppercase ${accentText}`}>{label}</p>
        {hint && <p className="mt-0.5 text-[11px] text-mist/50">{hint}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(Math.max(min, value - STEP))}
          className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist disabled:opacity-30"
        >
          −
        </button>
        <span className="w-14 text-center font-mono text-sm text-mist">${value}</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(value + STEP)}
          className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist disabled:opacity-30"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function PlanningStage({ onDeploy }) {
  const baseline = useMemo(() => computeBaselineSuggestions(), []);
  const [plan, setPlan] = useState(baseline);
  const [goalName, setGoalName] = useState("");

  const set = (key) => (val) => setPlan((p) => ({ ...p, [key]: val }));

  const totalAllocated =
    plan.essentials + plan.emergencyFund + plan.debtPayoff + plan.sinkingFund + plan.goals + plan.lifestyle + plan.discretionary;
  const remaining = MONTHLY_INCOME - totalAllocated;

  const essentialsTooLow = plan.essentials < ESSENTIALS_FLOOR;
  const goalNeedsName = plan.goals > 0 && goalName.trim().length === 0;
  const isZeroBased = remaining === 0;
  const canDeploy = isZeroBased && !essentialsTooLow && !goalNeedsName;

  const extraDebtPayment = Math.max(0, plan.debtPayoff - 35);
  const estAnnualInterestSaved = Math.round(extraDebtPayment * 12 * DEBT_APR);

  const emergencyPct = Math.min(100, Math.round((plan.emergencyFund / EMERGENCY_FUND_GOAL) * 100));

  const handleDeploy = () => {
    if (!canDeploy) return;
    onDeploy({ ...plan, goalName: goalName.trim() });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>THE BATTLE PLAN</span>
        <span>INCOME ${MONTHLY_INCOME.toLocaleString()}</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Every dollar is a soldier. Give each one a job below until nothing's left unassigned —
        that's a <span className="text-cyan">zero-based budget</span>.
      </p>

      {/* Live remaining tracker */}
      <div
        className={`mb-6 border-2 p-4 text-center transition-colors ${
          remaining === 0
            ? "border-lime bg-lime/10"
            : remaining > 0
            ? "border-gold bg-gold/10"
            : "border-danger bg-danger/10"
        }`}
      >
        <p className="label-pixel mb-1 text-mist/60">Amount Remaining</p>
        <p
          className={`font-display text-xl ${
            remaining === 0 ? "text-lime" : remaining > 0 ? "text-gold" : "text-danger"
          }`}
        >
          ${remaining.toLocaleString()}
        </p>
        <p className="mt-2 text-[11px] text-mist/60">
          {remaining > 0 &&
            `${remaining} AWOL soldiers — every dollar needs an order, or it goes missing like last month's did.`}
          {remaining < 0 &&
            "Over budget. Trim Discretionary or Lifestyle first — those are Wants and Variable costs, the easiest to cut without breaking anything essential."}
          {remaining === 0 && "Fully deployed. Every dollar has a job."}
        </p>
      </div>

      {/* Essentials */}
      <section className="panel mb-4 p-4">
        <p className="mb-1 flex items-center gap-2 font-display text-[10px] text-cyan">🏠 ESSENTIALS</p>
        <p className="mb-2 text-[11px] text-mist/50">Rent, utilities, groceries, gas, phone.</p>
        <StepperRow label="Monthly Allocation" value={plan.essentials} onChange={set("essentials")} min={0} accent="cyan" />
        {essentialsTooLow && (
          <p className="mt-2 text-[11px] text-danger">
            Too low — your actual fixed essentials run about ${ESSENTIALS_FLOOR}/mo. This isn't a
            place to guess.
          </p>
        )}
      </section>

      {/* Security */}
      <section className="panel mb-4 p-4">
        <p className="mb-1 font-display text-[10px] text-lime">🛡️ SECURITY</p>
        <p className="mb-2 text-[11px] text-mist/50">
          Stability first — this is what turns disasters into inconveniences.
        </p>

        <StepperRow
          label="Emergency Fund"
          hint={`Goal: $${EMERGENCY_FUND_GOAL} buffer · ${emergencyPct}% of first goal this month`}
          value={plan.emergencyFund}
          onChange={set("emergencyFund")}
          accent="lime"
        />
        <StepperRow
          label="Debt Payoff"
          hint={`Balance $${STARTING_DEBT} @ ${Math.round(DEBT_APR * 100)}% APR · min payment $35${
            extraDebtPayment > 0 ? ` · ~$${estAnnualInterestSaved}/yr interest saved` : ""
          }`}
          value={plan.debtPayoff}
          onChange={set("debtPayoff")}
          accent="lime"
        />
        <StepperRow
          label="Sinking Fund"
          hint="For irregular costs — car registration, gifts, annual renewals."
          value={plan.sinkingFund}
          onChange={set("sinkingFund")}
          accent="lime"
        />
      </section>

      {/* Goals */}
      <section className="panel mb-4 p-4">
        <p className="mb-1 font-display text-[10px] text-gold">🚩 GOALS</p>
        <p className="mb-2 text-[11px] text-mist/50">
          Optional this month — Essentials and Security come first. Name it, so it's harder to
          raid later.
        </p>
        <input
          type="text"
          placeholder="e.g. Down Payment, Spring Break Trip"
          value={goalName}
          onChange={(e) => setGoalName(e.target.value)}
          className="input-pixel mb-3 !py-2 text-xs"
        />
        <StepperRow label="Monthly Allocation" value={plan.goals} onChange={set("goals")} accent="gold" />
        {goalNeedsName && <p className="mt-2 text-[11px] text-danger">Give this goal a name before funding it.</p>}
      </section>

      {/* Lifestyle */}
      <section className="panel mb-4 p-4">
        <p className="mb-1 font-display text-[10px] text-magenta">🎽 LIFESTYLE</p>
        <p className="mb-2 text-[11px] text-mist/50">Gym, apparel, gifts, pet costs.</p>
        <StepperRow label="Monthly Allocation" value={plan.lifestyle} onChange={set("lifestyle")} accent="magenta" />
      </section>

      {/* Discretionary */}
      <section className="panel mb-6 p-4">
        <p className="mb-1 font-display text-[10px] text-danger">🎮 DISCRETIONARY</p>
        <p className="mb-2 text-[11px] text-mist/50">
          Games, dining out, treats. First place to cut if the plan runs over.
        </p>
        <StepperRow label="Monthly Allocation" value={plan.discretionary} onChange={set("discretionary")} accent="danger" />
      </section>

      <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
        {canDeploy ? "DEPLOY BATTLE PLAN ▶" : "COMPLETE THE PLAN TO DEPLOY"}
      </button>
    </div>
  );
}
